import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { ROUTE_CONSTANTS } from "./route-constants";
import LoginPage from "../features/LogInPage/LoginPage";
import Home from "../features/Home/Home";
import LocationsAndRooms from "../features/LocationsAndRooms/LocationsAndRooms";
import CallbackAPI from "../features/CallbackAPI/CallbackAPI";
import Topic from "../features/Topic/Topic";
import SessionView from "../features/SessionView/SessionView";
import Users from "../features/Users/Users";
import Sessions from "../features/Sessions/Sessions";
import Questions from "../components/Questions/Questions";
import BootcampModuleView from "../features/BootcampModuleView/BootcampModuleView";
import Attendees from "../features/AttendeesPage/Attendees";
import Dashboard from "../features/Dashboard/Dashboard";
import { useSelector } from "react-redux";
import { hasAnyPermission, ROLE_TYPES } from "../utils/utils";
import Exercises from "../features/Exercises/Exercises";
import AccessDenied from "../features/AccessDenied/AccessDenied";
import Performance from "../features/Performance/Performance";
import DynaciFormsListing from "../features/DynamicForms/dynamicFormsListing";
import AssessmentQuestions from "../features/Assessment Questions/AssessmentQuestions";
import AssessmentListing from "../features/AssessmentListing/AssessmentListing";
import CreateAssessment from "../features/CreateAssessment/CreateAssessment";
import ViewAssessment from "../features/ViewAssessment/ViewAssessment";
import EvaluationListing from "../features/EvaluationListing/EvaluationListing";
import ViewAssessmentEvaluation from "../features/ViewAssessmentEvaluation/ViewAssessmentEvaluation";
import Assessments from "../features/Assessments/Assessments";
import AssessmentPlay from "../features/AssessmentPlay/AssessmentPlay";
import NetworkError from "../features/NetworkError/NetworkError";
import MyForm from "../features/DynamicForms/MyForm/MyForm";
import PerformanceDashboard from "../features/Dashboard/PerformanceDashboard";
import MyFeedbacks from "../features/DynamicForms/MyForm/MyFeedbacks";
import MyFormAsMentor from "../features/DynamicForms/MyForm/MyFormAsMentor";

const hasStandardRole = (roles) => {
  return (ROLE_TYPES.isHr(roles) || ROLE_TYPES.isCompetencySpoc(roles) || ROLE_TYPES.isCompetencyLead(roles));
};

const isNonAdminUser = (roles) => {
  return ROLE_TYPES.isUser(roles) && !ROLE_TYPES.isAdmin(roles);
};

const canAccessHomeRoutes = (roles, permission) => {
  return (hasStandardRole(roles) || isNonAdminUser(roles) || hasAnyPermission(permission));
};

const canAccessAdminRoutes = (roles, permission) => {
  return (hasStandardRole(roles) || ROLE_TYPES.isAdmin(roles) || permission?.spoc);
};

const canAccessDashboard = (roles, isAdminOnly) => {
  return (hasStandardRole(roles) || isNonAdminUser(roles) || isAdminOnly);
};

const shouldRedirectToError = (roles) => {
  return (hasStandardRole(roles) || isNonAdminUser(roles));
};

const createProtectedRoute = (path, Component, isUserLoggedOut, redirectTo = "/login") => {
  return (<Route key={path}
    path={path}
    element={isUserLoggedOut ? <Navigate to={redirectTo} /> : <Component />} />);
};

const getHomeRoutes = (isUserLoggedOut) => {
  return [
    <Route key="home" path={ROUTE_CONSTANTS.HOME}
      element={isUserLoggedOut ? <LoginPage /> : <Home />} />,
    <Route key="bootcamp-assessment-view-eval" path={ROUTE_CONSTANTS.BOOTCAMP_ASSESSMENT_VIEW_EVALUATION_LISTING}
      element={isUserLoggedOut ? (<Navigate to="/login" />) : (<ViewAssessmentEvaluation />)} />,
    <Route key="module-assessment-view-eval" path={ROUTE_CONSTANTS.MODULE_ASSESSMENT_VIEW_EVALUATION_LISTING}
      element={isUserLoggedOut ? (<Navigate to="/login" />) : (<ViewAssessmentEvaluation />)} />
  ];
};

const getAdminRoutes = (isUserLoggedOut) => {
  return [
    createProtectedRoute(ROUTE_CONSTANTS.USERS, Users, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.LOCATIONS_AND_ROOMS, LocationsAndRooms, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.TOPIC, Topic, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.QUESTION_VIEW, Questions, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.ASSESSMENT_QUESTIONS, AssessmentQuestions, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.MODULE_CREATE_ASSESSMENT, CreateAssessment, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.BOOTCAMP_CREATE_ASSESSMENT, CreateAssessment, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.MODULE_VIEW_ASSESSMENT, ViewAssessment, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.BOOTCAMP_VIEW_ASSESSMENT, ViewAssessment, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.BOOTCAMP_ASSESSMENT_LISTING, AssessmentListing, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.MODULE_ASSESSMENT_LISTING, AssessmentListing, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.SESSIONS_QUESTION_VIEW, Questions, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.BOOTCAMP_ASSESSMENT_EVALUATION_LISTING, EvaluationListing, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.MODULE_ASSESSMENT_EVALUATION_LISTING, EvaluationListing, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.BOOTCAMP_ASSESSMENT_VIEW_EVALUATION_LISTING, ViewAssessmentEvaluation, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.MODULE_ASSESSMENT_VIEW_EVALUATION_LISTING, ViewAssessmentEvaluation, isUserLoggedOut)
  ];
};

const getPublicRoutes = (isUserLoggedOut) => {
  return [
    createProtectedRoute(ROUTE_CONSTANTS.BOOTCAMP_MODULE, BootcampModuleView, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.SESSIONS, Sessions, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.TOPIC_SESSIONS, Sessions, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.TOPIC_SESSION_VIEW, SessionView, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.SESSION_VIEW, SessionView, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.ATTENDEES_BOOTCAMP, Attendees, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.ATTENDEES_MODULE, Attendees, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.EXERCISES, Exercises, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.ASSESSMENTS, Assessments, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.MODULE_SESSIONS_QUESTION_VIEW, Questions, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.PERFORMANCE, Performance, isUserLoggedOut),
    createProtectedRoute(ROUTE_CONSTANTS.ASSESSMENT_PLAY, AssessmentPlay, isUserLoggedOut)
  ];
};

const createErrorRedirectRoute = (path, isUserLoggedOut) => {
  return (<Route key={path}
    path={path}
    element={isUserLoggedOut ? (<Navigate to="/login" />) : (<Navigate to="/error" />)} />);
};

const getErrorRedirectRoutes = (isUserLoggedOut) => {
  return [
    createErrorRedirectRoute(ROUTE_CONSTANTS.USERS, isUserLoggedOut),
    createErrorRedirectRoute(ROUTE_CONSTANTS.LOCATIONS_AND_ROOMS, isUserLoggedOut),
    createErrorRedirectRoute(ROUTE_CONSTANTS.TOPIC, isUserLoggedOut),
    createErrorRedirectRoute(ROUTE_CONSTANTS.QUESTION_VIEW, isUserLoggedOut),
    createErrorRedirectRoute(ROUTE_CONSTANTS.SESSIONS_QUESTION_VIEW, isUserLoggedOut),
    createErrorRedirectRoute(ROUTE_CONSTANTS.MODULE_SESSIONS_QUESTION_VIEW, isUserLoggedOut),
    createErrorRedirectRoute("*", isUserLoggedOut)
  ];
};

const AppSwitch = ({ isUserLoggedIn }) => {
  const isUserLoggedOut = !isUserLoggedIn;
  const { roles, permission } = useSelector((state) => state?.security);
  const isAdminOnly = ROLE_TYPES.isOnlyRole(roles, "ADMIN");

  const showHomeRoutes = canAccessHomeRoutes(roles, permission);
  const showAdminRoutes = canAccessAdminRoutes(roles, permission);
  const showDashboard = canAccessDashboard(roles, isAdminOnly);
  const showErrorRedirects = shouldRedirectToError(roles);

  return (
    <Routes>
      <Route path="/login/success/callback" element={<CallbackAPI />} />

      {isUserLoggedOut && <Route path="*" element={<Navigate to="/login" />} />}

      <Route path={ROUTE_CONSTANTS.LOGIN}
        element={isUserLoggedOut ? <LoginPage /> : <Navigate to="/" />} />
      <Route
        path={ROUTE_CONSTANTS.FORMS}
        element={isUserLoggedOut ? <Navigate to="/login" /> : <DynaciFormsListing />}
      />
      <Route
        path={ROUTE_CONSTANTS.MYFORMS}
        element={isUserLoggedOut ? <Navigate to="/login" /> : <MyForm />}
      />
      {showDashboard &&
        (<Route path={ROUTE_CONSTANTS.PERFORMANCE_DASHBOARD}
          element={isUserLoggedOut ? <Navigate to="/login" /> : <PerformanceDashboard />} />)}
      <Route
        path={ROUTE_CONSTANTS.MyFeedbacks}
        element={isUserLoggedOut ? <Navigate to="/login" /> : <MyFeedbacks />}
      />
      <Route
        path={ROUTE_CONSTANTS.FORM_RESPONSE_AS_MENTOR}
        element={isUserLoggedOut ? <Navigate to="/login" /> : <MyFormAsMentor />}
      />
      {showHomeRoutes && getHomeRoutes(isUserLoggedOut)}

      {showAdminRoutes && getAdminRoutes(isUserLoggedOut)}

      {permission?.spoc &&
        (<Route path={ROUTE_CONSTANTS.TOPIC}
          element={isUserLoggedOut ? <Navigate to="/login" /> : <Topic />} />)}

      {showDashboard &&
        (<Route path={ROUTE_CONSTANTS.DASHBOARD}
          element={isUserLoggedOut ? <Navigate to="/login" /> : <Dashboard />} />)}

      {getPublicRoutes(isUserLoggedOut)}

      {showErrorRedirects && getErrorRedirectRoutes(isUserLoggedOut)}

      {isAdminOnly &&
        (<Route path="*"
          element={isUserLoggedOut ? (<Navigate to="/login" />) : (<Navigate to="/dashboard" />)} />)}

      <Route path={ROUTE_CONSTANTS.NETWORK_ERROR}
        element={isUserLoggedOut ? <NetworkError /> : <Navigate to="/" replace />} />

      {!isUserLoggedOut && (<Route path={ROUTE_CONSTANTS.ACCESS_DENIED} element={<AccessDenied />} />)}
    </Routes>
  );
};

export default AppSwitch;
